import {
  Modal,
  type ModalBaseCloseButtonProps,
  Text,
  TextInput,
} from '@mantine/core';
import {BasicButton} from '@packages/ui';
import {type FC, type FormEvent, useId} from 'react';
import {LIIGA_HOST} from '../../../../../config/constants/hosts';
import {MICROSOFT_MAIL_VALIDATION_MSG} from '../../../../../core/entities/mypage/schema';
import {
  EMAIL_INPUT_ERROR_TYPE,
  type EmailInputErrorType,
} from '../../../../../core/entities/register/constants';
import type {Recruitment} from '../../../../../core/repositories/recruitingInfoRepository';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {modalConfig} from '../../../../../shared/components/modalConfig';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {Deadline} from './Deadline';

export const ENTRY_REGISTER_MODAL_COMPONENT_ID =
  'recruiting-info-entry-register';

const EMAIL_ERROR_MESSAGE: Record<EmailInputErrorType, string> = {
  [EMAIL_INPUT_ERROR_TYPE.FORMAT_ERROR]: 'メールアドレスの形式が不正です',
  [EMAIL_INPUT_ERROR_TYPE.SERVER_ERROR]:
    '登録できませんでした。ご記入に誤りがないかご確認ください。',
  [EMAIL_INPUT_ERROR_TYPE.LENGTH_ERROR]: '100文字以内で入力してください',
  [EMAIL_INPUT_ERROR_TYPE.CONFLICT_ERROR]:
    '登録済みのメールアドレスです。ログインしてエントリーへお進みください。',
  [EMAIL_INPUT_ERROR_TYPE.MICROSOFT_ERROR]: MICROSOFT_MAIL_VALIDATION_MSG,
  [EMAIL_INPUT_ERROR_TYPE.UNIVERSITY_ERROR]:
    'ご所属の大学のメールアドレスは外資就活ドットコムに対応していない可能性があります。',
  [EMAIL_INPUT_ERROR_TYPE.NONE]: '',
};

// 会員登録は外資就活ネクストのアカウントにもなるため、/mail_auth と同じ4つを同意対象として示す
const stage = process.env.NEXT_PUBLIC_RELEASE_STAGE;
const liigaHost = LIIGA_HOST[stage ?? 'production'] ?? LIIGA_HOST.production;
const AGREEMENT_LINKS = [
  {href: '/terms', label: '外資就活ドットコム利用規約'},
  {href: '/service_privacy_policy', label: 'プライバシーポリシー'},
  {href: `${liigaHost}/terms`, label: '外資就活ネクスト利用規約'},
  {href: `${liigaHost}/privacy`, label: '個人情報保護方針'},
] as const;

type Props = {
  opened: boolean;
  onClose: () => void;
  recruitmentTitle: string;
  schedule?: Recruitment['schedules'][number];
  loginUrl: string;
  email: string;
  onEmailChange: (email: string) => void;
  errorType: EmailInputErrorType;
  isSubmitting: boolean;
  onSubmit: () => void;
};

export const EntryRegisterModalPresenter: FC<Props> = ({
  opened,
  onClose,
  recruitmentTitle,
  schedule,
  loginUrl,
  email,
  onEmailChange,
  errorType,
  isSubmitting,
  onSubmit,
}) => {
  const {isPC} = useMediaQuery();
  const headingId = useId();

  // ModalBaseCloseButtonProps は data 属性を実際には通すが、超過プロパティチェックに弾かれるためアサーションで抑える
  const closeButtonProps = {
    'aria-label': '閉じる',
    'data-gtm-click': `close-${ENTRY_REGISTER_MODAL_COMPONENT_ID}`,
  } as ModalBaseCloseButtonProps;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <Modal.Root
      className="w-[343px] pc:w-[560px]"
      radius={16}
      opened={opened}
      onClose={onClose}
      centered={modalConfig.centered}
      padding={modalConfig.padding}
      styles={{
        // 見出しは本文側に置くため、ヘッダーは閉じるボタンだけを右寄せで出す
        header: {padding: 12, paddingBottom: 0, justifyContent: 'flex-end'},
      }}
    >
      <Modal.Overlay />
      <Modal.Content aria-labelledby={headingId}>
        <Modal.Header>
          <Modal.CloseButton {...closeButtonProps} />
        </Modal.Header>
        <Modal.Body>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-16 rounded-16 bg-white p-20 pt-0 pc:p-24 pc:pt-0"
          >
            <div
              className="flex flex-col items-center gap-4 text-center"
              data-gtm-imp={`imp_${ENTRY_REGISTER_MODAL_COMPONENT_ID}`}
            >
              <Text
                component="h2"
                id={headingId}
                className="text-lg pc:text-xl font-bold leading-sm text-black-1"
              >
                エントリーには会員登録が必要です
              </Text>
              <Text className="text-sm text-black-2">
                会員登録（無料）を済ませると、この募集にエントリーできます。
              </Text>
            </div>

            <div className="flex flex-col gap-4 rounded-12 bg-background-1 p-12">
              <Text className="text-base font-bold leading-sm text-black-1 line-clamp-2">
                {recruitmentTitle}
              </Text>
              {schedule && <Deadline schedule={schedule} layout="text" />}
            </div>

            <div className="flex flex-col gap-8">
              <Text
                component="h3"
                className="text-base font-bold leading-sm text-black-1"
              >
                メールアドレスで登録手続きを始める
              </Text>
              <TextInput
                type="email"
                aria-label="メールアドレス"
                placeholder="gaishishukatsu@gmail.com"
                autoComplete="email"
                value={email}
                error={EMAIL_ERROR_MESSAGE[errorType]}
                disabled={isSubmitting}
                onChange={(e) => onEmailChange(e.currentTarget.value)}
                // SPで入力時にズームされることを防ぐ
                size={isPC ? 'sm' : 'md'}
              />
            </div>

            <BasicButton
              type="submit"
              label="登録手続きを始める"
              className="w-full"
              isPrimary
              isLarge
              isDisabled={isSubmitting}
              dataGtmClick={`submit-${ENTRY_REGISTER_MODAL_COMPONENT_ID}`}
            />

            <div className="flex flex-col gap-4 text-center">
              <Text className="text-xs text-black-3">
                登録すると
                {AGREEMENT_LINKS.map((link, i) => (
                  <span key={link.href}>
                    {i > 0 && '・'}
                    <CommonLink
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-2 hover:underline"
                    >
                      {link.label}
                    </CommonLink>
                  </span>
                ))}
                に同意したものとみなします
              </Text>
              <Text className="text-sm text-black-2">
                会員の方は
                <CommonLink
                  href={loginUrl}
                  className="ml-4 font-bold text-primary-2 hover:underline"
                  data-gtm-click={`login-${ENTRY_REGISTER_MODAL_COMPONENT_ID}`}
                >
                  ログイン
                </CommonLink>
              </Text>
            </div>
          </form>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
